import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertPhoto as ImageIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CreateNewFolder as CreateFolderIcon,
  Upload as UploadIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';

const ImagesTab = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadImages();
  }, [currentPath]);

  const loadImages = async () => {
    try {
      setLoading(true);
      const response = await SitesService.listImages(currentPath);
      console.log('📁 [ImagesTab] Response:', response);
      console.log('📁 [ImagesTab] Folders:', response.folders);
      console.log('📁 [ImagesTab] Files:', response.files);
      setFolders(response.folders || []);
      setFiles(response.files || []);
    } catch (error) {
      console.error('Error loading images:', error);
      SnackbarUtils.error(
        error.response?.data?.message ||
        error.message ||
        t('error_loading_images') ||
        'Error al cargar las imágenes'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      SnackbarUtils.warning(t('folder_name_required') || 'El nombre de la carpeta es requerido');
      return;
    }

    try {
      await SitesService.createFolder(newFolderName.trim(), currentPath);
      SnackbarUtils.success(t('folder_created') || 'Carpeta creada exitosamente');
      setCreateFolderOpen(false);
      setNewFolderName('');
      loadImages();
    } catch (error) {
      console.error('Error creating folder:', error);
      SnackbarUtils.error(
        error.response?.data?.message ||
        error.message ||
        t('error_creating_folder') ||
        'Error al crear la carpeta'
      );
    }
  };

  const handleUploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tamaño (2MB)
    if (file.size > 2 * 1024 * 1024) {
      SnackbarUtils.error(t('file_too_large') || 'El archivo debe ser menor a 2MB');
      return;
    }

    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      SnackbarUtils.error(t('invalid_image_format') || 'El archivo debe ser una imagen');
      return;
    }

    try {
      setUploading(true);
      await SitesService.uploadImage(file, currentPath);
      SnackbarUtils.success(t('image_uploaded') || 'Imagen subida exitosamente');
      loadImages();
    } catch (error) {
      console.error('Error uploading image:', error);
      SnackbarUtils.error(
        error.response?.data?.message ||
        error.message ||
        t('error_uploading_image') ||
        'Error al subir la imagen'
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await SitesService.deleteImage(itemToDelete.path);
      SnackbarUtils.success(t('item_deleted') || 'Elemento eliminado exitosamente');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      loadImages();
    } catch (error) {
      console.error('Error deleting item:', error);
      SnackbarUtils.error(
        error.response?.data?.message ||
        error.message ||
        t('error_deleting_item') ||
        'Error al eliminar el elemento'
      );
    }
  };

  const navigateToFolder = (folderPath) => {
    setCurrentPath(folderPath);
  };

  const navigateUp = () => {
    if (!currentPath) return;
    const pathParts = currentPath.split('/');
    pathParts.pop();
    setCurrentPath(pathParts.join('/'));
  };

  const getBreadcrumbs = () => {
    const parts = currentPath ? currentPath.split('/').filter(Boolean) : [];
    return [
      { name: t('images') || 'Imágenes', path: '' },
      ...parts.map((part, index) => ({
        name: part,
        path: parts.slice(0, index + 1).join('/')
      }))
    ];
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            {t('manage_images') || 'Gestionar Imágenes'}
          </Typography>
          <Breadcrumbs sx={{ mt: 1 }}>
            {getBreadcrumbs().map((crumb, index) => (
              <Link
                key={index}
                component="button"
                variant="body2"
                onClick={() => setCurrentPath(crumb.path)}
                sx={{
                  cursor: 'pointer',
                  textDecoration: index === getBreadcrumbs().length - 1 ? 'none' : 'underline',
                  color: index === getBreadcrumbs().length - 1 ? 'text.primary' : 'primary.main'
                }}
              >
                {crumb.name}
              </Link>
            ))}
          </Breadcrumbs>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {currentPath && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={navigateUp}
              variant="outlined"
            >
              {t('back') || 'Volver'}
            </Button>
          )}
          <Button
            startIcon={<CreateFolderIcon />}
            onClick={() => setCreateFolderOpen(true)}
            variant="outlined"
          >
            {t('create_folder') || 'Crear Carpeta'}
          </Button>
          <Button
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            variant="contained"
            disabled={uploading}
          >
            {uploading ? (t('uploading') || 'Subiendo...') : (t('upload_image') || 'Subir Imagen')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleUploadImage}
          />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : folders.length === 0 && files.length === 0 ? (
        <Alert severity="info">
          {t('no_images_found') || 'No hay imágenes en esta carpeta'}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {/* Carpetas */}
          {folders.map((folder) => {
            console.log('📁 [ImagesTab] Rendering folder:', folder);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={folder.path}>
                <Card>
                  <CardActionArea 
                    onClick={() => navigateToFolder(folder.path)}
                    sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                      minHeight: 120
                    }}
                  >
                    <FolderIcon sx={{ fontSize: 64, color: 'primary.main', mb: 1 }} />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'text.primary',
                        fontWeight: 500,
                        textAlign: 'center',
                        width: '100%',
                        wordBreak: 'break-word'
                      }}
                      title={folder.name || folder.path}
                    >
                      {folder.name || folder.path || 'Sin nombre'}
                    </Typography>
                  </CardActionArea>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setItemToDelete({ ...folder, type: 'folder' });
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              </Grid>
            );
          })}

          {/* Imágenes */}
          {files.map((file) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={file.path}>
              <Card>
                <CardMedia
                  component="img"
                  height="200"
                  image={file.url}
                  alt={file.name}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent>
                  <Typography variant="body2" noWrap title={file.name}>
                    {file.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                    <Chip label={formatFileSize(file.size)} size="small" />
                    <Chip label={file.mimeType.split('/')[1]} size="small" variant="outlined" />
                  </Box>
                </CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      setItemToDelete({ ...file, type: 'file' });
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog para crear carpeta */}
      <Dialog open={createFolderOpen} onClose={() => setCreateFolderOpen(false)}>
        <DialogTitle>{t('create_folder') || 'Crear Carpeta'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('folder_name') || 'Nombre de la carpeta'}
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderOpen(false)}>
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button onClick={handleCreateFolder} variant="contained">
            {t('create') || 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para confirmar eliminación */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('confirm_delete') || 'Confirmar Eliminación'}</DialogTitle>
        <DialogContent>
          <Typography>
            {itemToDelete?.type === 'folder'
              ? t('confirm_delete_folder') || '¿Está seguro de que desea eliminar esta carpeta y todo su contenido?'
              : t('confirm_delete_image') || '¿Está seguro de que desea eliminar esta imagen?'}
          </Typography>
          {itemToDelete && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {itemToDelete.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('delete') || 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ImagesTab;

